import styled, { css } from 'styled-components';
import { MEDIA_QUERY } from '@optimus-web/breakpoints';
import { ELLIPSIS, getSpacingToken, getTextTokenPreset, getToken, Minifiable } from '@optimus-web/common';
import { Icon } from '../Icon';

interface ProgressIndicatorContainerProps {
    vertical?: boolean;
}

export const ProgressIndicatorContainer = styled.ol<ProgressIndicatorContainerProps>`
    width: 100%;
    margin: 0;
    padding: ${getSpacingToken('100')}px 0;
    list-style: none;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    ${({ vertical }) =>
        vertical &&
        css`
            flex-direction: column;
            gap: ${getSpacingToken('50')}px;
        `}
`;

interface StepsWrapperProps {
    vertical?: boolean;
}

export const StepsWrapper = styled.div<StepsWrapperProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    ${({ vertical }) =>
        !vertical &&
        css`
            flex-direction: row;
            flex-wrap: nowrap;
            align-items: center;
        `}
`;

export const OpenableVerticalStepperContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0 ${getSpacingToken('200')}px;
`;

export const ToggleButtonElement = styled.button<Minifiable>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border: 0;
    background: transparent;
    ${({ small }) =>
        small
            ? css`
                  padding: ${getSpacingToken('100')}px 0;
              `
            : css`
                  padding: ${getSpacingToken('200')}px 0;
              `}
    cursor: pointer;
    order: 1;
    color: ${getToken('textStaticPrimary')};
`;

export const StepCountElement = styled.div`
    ${getTextTokenPreset('bodyMediumStrong')};
    color: ${getToken('textStaticTertiary')};
    margin-right: ${getSpacingToken('100')}px;
    min-width: ${getSpacingToken('1000')}px;
    text-align: right;
`;

interface StepElementProps extends Minifiable {
    vertical?: boolean;
    clickable?: boolean;
    active?: boolean;
    completed?: boolean;
}

export const StepElement = styled.button<StepElementProps>`
    display: flex;
    flex-direction: column;
    gap: ${getSpacingToken('100')}px;
    align-items: center;
    box-sizing: border-box;
    background: transparent;
    border: 0;
    text-align: center;
    ${({ clickable }) =>
        clickable &&
        css`
            &:hover {
                ${StepPrimaryTextElement} {
                    text-decoration: ${getToken('textDecorationUnderline')};
                }

                ${StepNumberElement} {
                    background: ${getToken('backgroundInteractiveNeutralHover')};
                }
            }

            &:active {
                ${StepNumberElement} {
                    background: ${getToken('backgroundInteractiveNeutralActive')};
                }
            }
        `}

    ${({ completed }) =>
        completed &&
        css`
            &:hover {
                ${StepPrimaryTextElement} {
                    text-decoration: ${getToken('textDecorationUnderline')};
                }

                ${StepIconElement} {
                    background: ${getToken('backgroundInteractiveSecondaryHover')};
                    color: ${getToken('textInteractivePrimaryHover')};
                }
            }

            &:active {
                ${StepIconElement} {
                    background: ${getToken('backgroundInteractiveSecondaryActive')};
                    color: ${getToken('textInteractivePrimaryActive')};
                }
            }
        `}

    ${({ vertical }) =>
        vertical &&
        css`
            width: 100%;
            flex-direction: row;
            text-align: left;
        `}

    ${({ active, clickable, completed }) =>
        (clickable || completed) &&
        !active &&
        css`
            cursor: pointer;
            padding: 0;
        `}
`;

interface StepTextWrapperProps {
    active?: boolean;
    vertical?: boolean;
}

export const StepTextWrapper = styled.div<StepTextWrapperProps>`
    overflow: auto;
    ${({ vertical }) =>
        !vertical &&
        css`
            min-width: ${getSpacingToken('700')}px;
        `}

    ${MEDIA_QUERY.tabletUp} {
        display: block;
    }
`;

interface StepPrimaryTextElementProps {
    clickable?: boolean;
    completed?: boolean;
    active?: boolean;
}

export const StepPrimaryTextElement = styled.div<StepPrimaryTextElementProps>`
    ${getTextTokenPreset('bodyMediumStrong')};

    color: ${getToken('textStaticPrimary')};

    ${({ active, completed, clickable }) =>
        !active &&
        !completed &&
        !clickable &&
        css`
            color: ${getToken('textStaticTertiary')};
        `}
    ${ELLIPSIS}
`;

export const StepSecondaryTextElement = styled.div`
    ${getTextTokenPreset('bodySmall')};
    color: ${getToken('textStaticTertiary')};
    ${ELLIPSIS}
`;

interface StepNumberIconProps {
    completed?: boolean;
    active?: boolean;
    primary?: boolean;
    singleItem?: boolean;
    clickable?: boolean;
}

export const StepIconElement = styled(Icon)<StepNumberIconProps>`
    color: ${(props) => (props.primary ? getToken('textInteractivePrimaryDefault') : getToken('textStaticPrimary'))};
    border-radius: ${getToken('borderRadiusRound')};
    ${(props) =>
        props.active &&
        !props.singleItem &&
        css`
            background: ${getToken('backgroundInteractiveSecondaryDefault')};
            box-shadow: 0 0 0 ${getSpacingToken('100')}px ${getToken('backgroundInteractiveSecondaryDefault')};
        `}
    ${(props) =>
        (props.completed || (props.completed && props.clickable)) &&
        css`
            background: ${getToken('backgroundInteractiveSecondaryDefault')};
            width: ${getSpacingToken('300')}px;
            height: ${getSpacingToken('300')}px;
            display: flex;
            align-items: center;
            justify-content: center;
        `}
`;

export const StepNumberElement = styled.span<StepNumberIconProps>`
    box-sizing: border-box;
    ${getTextTokenPreset('bodyMediumStrong')};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    margin: ${({ active, completed, clickable }) => (completed || active || clickable ? 0 : getSpacingToken('100'))}px;
    width: ${({ active, completed, clickable }) =>
        completed || active || clickable ? getSpacingToken('300') : getSpacingToken('100')}px;
    height: ${({ active, completed, clickable }) =>
        completed || active || clickable ? getSpacingToken('300') : getSpacingToken('100')}px;
    border: ${(props) => (!props.active && !props.clickable ? `solid` : '0')};
    border-width: ${(props) => (!props.active && !props.clickable ? getToken('borderWidth150') : 0)}px;
    border-color: ${getToken('borderStaticPrimary')};
    border-radius: ${getToken('borderRadiusRound')};
    ${(props) =>
        props.active &&
        css`
            background-color: ${getToken('backgroundInteractivePrimaryDefault')};
        `};

    ${(props) =>
        props.clickable &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralDefault')};
        `};

    ${(props) =>
        props.active &&
        css`
            color: ${getToken('textStaticInverse')};
        `};
`;

interface StepLineElementProps {
    vertical?: boolean;
    primary?: boolean;
}

export const StepLineElement = styled.li<StepLineElementProps>`
    width: ${getToken('borderWidth150')}px;
    height: ${getSpacingToken('400')}px;
    margin-left: calc((${getSpacingToken('300')}px - ${getToken('borderWidth150')}px) / 2);
    background: ${(props) =>
        props.primary ? getToken('borderInteractivePrimaryDefault') : getToken('borderStaticPrimary')};

    ${({ vertical }) =>
        !vertical &&
        css`
            min-width: ${getSpacingToken('200')}px;
            height: ${getToken('borderWidth150')}px;
            flex-grow: 1;
            margin-left: 0;
            margin-top: calc((${getSpacingToken('300')}px - ${getToken('borderWidth150')}px) / 2);
        `}
`;
